import React, { useEffect, useState } from 'react';

import { withStyles } from '@mui/styles';

import { Tab, Tabs, Paper, Input, Radio, Button, Tooltip, Popover, RadioGroup, FormControlLabel } from '@mui/material';

import tobeor from 'tobeor';

import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';

import Loader from '../../Loader';
import UploadForm from '../../UploadForm';
import CommonStyles from '../../CommonStyles';
import TrplSelect from '../../TrplControls/TrplSelect';

import ChatStyles from './ChatStyles';
import ChatMessages from './ChatMessages';

import { getUrlExtension } from '../../../utils';

const styles = { ...ChatStyles, ...CommonStyles };

export default withStyles(styles)(
  ({
    fbInfo,
    tgInfo,
    smsInfo,
    webInfo,
    account,
    classes,
    chatTabs,
    subsData,
    emailInfo,
    getDraftMsg,
    contactName,
    loadMoreMsgs,
    chatTemplate,
    msgTemplates,
    chatMsgLoaded,
    imgUploadForm,
    setupAccounts,
    onInputChange,
    hotkeysPressed,
    onChatTabClick,
    onSendMsgClick,
    selectedContact,
    scrollToLastMsg,
    onImageUploadCb,
    isChatInfoValid,
    onRemovePidClick,
    onSendEmailClick,
    onUnseenMsgClick,
    chatTemplatePids,
    onChatHotkeyPress,
    onChatRadioSelect,
    unseenMsgCounters,
    onChatSettingsOpen,
    chatSettingsAnchor,
    onChatSelectChange,
    onChatFbInfoChange,
    onChatTgInfoChange,
    onChatSettingsClose,
    onChatRecipientMute,
    onSendMsgChangeStatus,
    onChatRecipientUnmute,
    onChatRecepientClick,
    toggleUploadImgModal,
    toggleDeleteMsgModal,
    isSendPopoverVisible,
    onChatEmailInputChange,
    removeUploadedTempImage,
    onChatTelephoneInputChange,
    onChatRecepientInHeaderClick,
  }) => {
    const [ isSendingMsg, setIsSendingMsg ] = useState(false);

    useEffect(() => {
      onSendMsgChangeStatus(isSendingMsg);
    }, [isSendingMsg]);

    const selectedContactId = selectedContact ? selectedContact.id : null;
    const messages = selectedContact ? selectedContact.msgs : null;

    const chatMsg = getDraftMsg(selectedContactId);

    const isTabSelected = (tab) => chatTabs.find((chatTab) => chatTab.value === tab && chatTab.isSelected === true);

    const isTgSelected = isTabSelected('telegram');
    const isEmailSelected = isTabSelected('email');
    const isSmsSelected = isTabSelected('sms');
    const isFbSelected = isTabSelected('facebook');
    const isWebSelected = isTabSelected('web');

    const isContactMuted = localStorage.getItem(`chat.notifications.${selectedContactId}`) === 'muted';

    const isSendInfoValid = isEmailSelected
      ? emailInfo.from && emailInfo.to && !isChatInfoValid.from && !isChatInfoValid.to
      : isSmsSelected
      ? smsInfo.from && smsInfo.to
      : isFbSelected
      ? fbInfo.from && fbInfo.to
      : isTgSelected
      ? tgInfo.from && tgInfo.to
      : isWebSelected
      ? true  // Web channel doesn't need from/to validation
      : false;

    const isDisabled = isSendingMsg || (!chatMsg.length && !imgUploadForm.images.length) || !isSendInfoValid;

    const selectedTab = chatTabs.find((tab) => tab.isSelected);

    const isTabsPresent = chatTabs.length > 1;
    let currentTime = null;

    const sendMsg = (e) => {
      if (isDisabled) return;

      setIsSendingMsg(true);

      const contactInfo = {
        selectedContactId,
        chatTemplatePids,
        selectedTab,
        emailInfo: emailInfo,
        smsInfo: smsInfo,
        fbInfo: fbInfo,
        tgInfo: tgInfo,
        webInfo: webInfo,
      };

      return isEmailSelected ? onSendEmailClick(e, contactInfo, () => { setIsSendingMsg(false) })
        : onSendMsgClick(e, contactInfo, () => { setIsSendingMsg(false) });
    };

    const chatHotkeys = account && +account.chat_hotkeys;

    const sendWithEnterKeyDown = (e) => {
      const { key } = e;

      if (key === 'Enter' && !hotkeysPressed.enter && !hotkeysPressed.shift) {
        e.preventDefault();

        if (chatMsg.trim().length) {
          sendMsg(e);
        }

        onChatHotkeyPress('enter', true);
      }

      if (key === 'Shift' && !hotkeysPressed.shift) onChatHotkeyPress('shift', true);
    };

    const sendWithEnterKeyUp = (e) => {
      const { key } = e;

      if (key === 'Shift') onChatHotkeyPress('shift', false);
      if (key === 'Enter') onChatHotkeyPress('enter', false);
    };

    const sendWithShiftEnterKeyDown = (e) => {
      const { key } = e;

      if (key === 'Shift' && !hotkeysPressed.shift) onChatHotkeyPress('shift', true);

      if (key === 'Enter' && !hotkeysPressed.enter) onChatHotkeyPress('enter', true);
    };

    const sendWithShiftEnterKeyUp = (e) => {
      const { key } = e;

      if (hotkeysPressed.shift && hotkeysPressed.enter) {
        if (chatMsg.trim().length) {
          sendMsg(e);
        }
      }

      if (key === 'Shift') onChatHotkeyPress('shift', false);
      if (key === 'Enter') onChatHotkeyPress('enter', false);
    };

    const sendWithCtrlEnterKeyDown = (e) => {
      const { key } = e;

      if (e.ctrlKey && !hotkeysPressed.ctrl) onChatHotkeyPress('ctrl', true);

      if (key === 'Enter' && !hotkeysPressed.enter) onChatHotkeyPress('enter', true);
    };

    const sendWithCtrlEnterKeyUp = (e) => {
      const { key } = e;

      if (hotkeysPressed.ctrl && hotkeysPressed.enter) {
        if (chatMsg.trim().length) {
          sendMsg(e);
        }
      }

      if (e.ctrlKey) onChatHotkeyPress('ctrl', false);
      if (key === 'Enter') onChatHotkeyPress('enter', false);
    };

    const getChannelIcon = (type) => {
      switch(type.toLowerCase()) {
        case 'sms':
          return 'fas fa-mobile-alt';
        case 'telegram':
          return 'fab fa-telegram-plane';
        case 'facebook':
          return 'fab fa-facebook-f';
        case 'web':
          return 'fas fa-globe';
        case 'email':
        default:
          return 'fas fa-envelope';
      }
    };

    const getTabLabel = (classes, tab) => (
      <div className={classes.chatTabLabel}>
        <i className={`${getChannelIcon(tab.value)} ${classes.chatTabIcon}`} />
        <span style={{ paddingLeft: '5px' }}>{tab.value}</span>
        {unseenMsgCounters && unseenMsgCounters[tab.value.toUpperCase()] > 0 && (
          <div className={classes.chatTabUnreadMessages}>{unseenMsgCounters[tab.value.toUpperCase()]}</div>
        )}
      </div>
    );

    const tabStyles = {
      maxWidth: 'none',
      minHeight: 'auto',
      height: '26px',
    };

    const msgTemplatesValues = [{ title: 'none', value: 'none' }];

    msgTemplates.forEach((tmplt) => {
      msgTemplatesValues.push({
        title: tmplt.title,
        value: tmplt.id,
      });
    });

    // Ensure chatTemplate is always a valid value
    const validChatTemplate = msgTemplatesValues.some(t => t.value === chatTemplate) ? chatTemplate : 'none';

    const location = window.location.href;
    let hideChatRecipientForm = false;
    if (location.split('/').includes('addon')) {
      hideChatRecipientForm = true;
    }

    return (
      <Paper className={classes.chat}>
        <UploadForm
          fields={{
            action: 'DIALOG_IMAGE',
          }}
          isOpen={imgUploadForm.isOpened}
          uploadUrl={`/uploadimage`}
          //acceptTypes="image/jpg,image/png,image/jpeg,application/pdf"
          updateParentData={onImageUploadCb}
          onClose={() => toggleUploadImgModal(false)}
        />

        <div className={classes.chatMessagesRow}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className={classes.chatRecipient} style={{ pointerEvents: hideChatRecipientForm ? 'none' : '' }} onClick={() => onChatRecepientInHeaderClick(selectedContactId)}>
              {contactName}
            </div>
            <div style={{ marginRight: '20px', marginTop: '3px', marginLeft: '12px' }}>
              {isContactMuted ?
                <Tooltip
                  title='Enable notifications'
                  placement='bottom'
                  classes={{
                    tooltip: classes.muiTooltip,
                  }}
                >
                  <VolumeOffRoundedIcon
                    className={classes.muteContactIcon}
                    onClick={(e) => {
                      e.preventDefault();
                      onChatRecipientUnmute(selectedContactId);
                      this.forceUpdate();
                    }}
                  />
                
                </Tooltip>
              : 
                <Tooltip
                  title='Disable notifications'
                  placement='bottom'
                  classes={{
                    tooltip: classes.muiTooltip,
                  }}
                >
                  <VolumeUpRoundedIcon
                    className={classes.muteContactIcon}
                    onClick={(e) => {
                      e.preventDefault();
                      onChatRecipientMute(selectedContactId);
                      this.forceUpdate();
                    }}
                  />
                
                </Tooltip>
              }
            </div>
          </div>

          {isTabsPresent ? (
            <Tabs
              style={{
                minHeight: 'auto',
              }}
              textColor="primary"
              variant="fullWidth"
              indicatorColor="primary"
              value={selectedTab.value}
              classes={{
                indicator: classes.chatTabIndicator,
              }}
              onChange={(e, value) => onChatTabClick(value)}
            >
              {chatTabs.map((tab) => {
                return <Tab key={tab.value} value={tab.value} label={getTabLabel(classes, tab)} style={tabStyles} />;
              })}
            </Tabs>
          ) : null}

          <ChatMessages
            classes={classes}
            account={account}
            messages={messages}
            subsData={subsData}
            currentTime={currentTime}
            loadMoreMsgs={loadMoreMsgs}
            isTabsPresent={isTabsPresent}
            chatMsgLoaded={chatMsgLoaded}
            setupAccounts={setupAccounts}
            scrollToLastMsg={scrollToLastMsg}
            onUnseenMsgClick={onUnseenMsgClick}
            toggleDeleteMsgModal={toggleDeleteMsgModal}
            onChatRecepientClick={onChatRecepientClick}
          />
        </div>

        <div className={classes.chatInfoContainer}>
          <form>
            <table className={classes.chatInfoTable}>
              {isEmailSelected ? (
                <tbody>
                  <tr>
                    <td>
                      <span
                        style={{ fontSize: '12px' }}
                        className={`${classes.chatInfoText} ${isChatInfoValid.to ? classes.chatInfoInputError : ''}`}
                      >
                        To:{' '}
                      </span>

                      <input
                        value={emailInfo.to}
                        onChange={(text) => {
                          onChatEmailInputChange('to', text);
                        }}
                        className={`${classes.chatInfoInput} ${isChatInfoValid.to ? classes.chatInfoInputError : ''}`}
                        name="to"
                      />
                    </td>

                    <td>
                      <span className={classes.chatInfoText}>Subject: </span>

                      <input
                        value={emailInfo.subject}
                        onChange={(text) => {
                          onChatEmailInputChange('subject', text);
                        }}
                        className={classes.chatInfoInput}
                        name="subject"
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <span
                        className={`${classes.chatInfoText} ${isChatInfoValid.from ? classes.chatInfoInputError : ''}`}
                      >
                        From:{' '}
                      </span>

                      <input
                        value={emailInfo.from}
                        onChange={(text) => {
                          onChatEmailInputChange('from', text);
                        }}
                        className={`${classes.chatInfoInput} ${isChatInfoValid.from ? classes.chatInfoInputError : ''}`}
                        name="from"
                      />
                    </td>

                    <td>
                      <span
                        className={`${classes.chatInfoText} ${
                          isChatInfoValid.replyTo ? classes.chatInfoInputError : ''
                        }`}
                      >
                        Reply to:{' '}
                      </span>

                      <input
                        value={emailInfo.replyTo}
                        onChange={(text) => {
                          onChatEmailInputChange('replyTo', text);
                        }}
                        className={`${classes.chatInfoInput} ${
                          isChatInfoValid.replyTo ? classes.chatInfoInputError : ''
                        }`}
                        name="replyto"
                      />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td>
                      <span className={`${classes.chatInfoText}`}>From: </span>

                      <input
                        value={
                          isSmsSelected ? smsInfo.from : isFbSelected ? fbInfo.from : isTgSelected ? tgInfo.from : ''
                        }
                        name="from"
                        onChange={(text) => {
                          if (isSmsSelected) {
                            onChatTelephoneInputChange('from', text);
                          } else if (isFbSelected) {
                            onChatFbInfoChange('from', text);
                          } else if (isTgSelected) {
                            onChatTgInfoChange('from', text);
                          }
                        }}
                        className={classes.chatInfoInput}
                      />
                    </td>

                    <td>
                      <span className={`${classes.chatInfoText}`}>To: </span>

                      <input
                        name="to"
                        value={isSmsSelected ? smsInfo.to : isFbSelected ? fbInfo.to : isTgSelected ? tgInfo.to : ''}
                        onChange={(text) => {
                          if (isSmsSelected) {
                            onChatTelephoneInputChange('to', text);
                          } else if (isFbSelected) {
                            onChatFbInfoChange('to', text);
                          } else if (isTgSelected) {
                            onChatTgInfoChange('to', text);
                          }
                        }}
                        className={classes.chatInfoInput}
                      />
                    </td>
                  </tr>
                </tbody>
              )}
            </table>

            <div className={classes.chatImagesToUpload}>
              {imgUploadForm.images.map((img, imgIndex) => {
                const isImage = img.url && tobeor(getUrlExtension(img.url), ['png','jpeg','jpg']);
                return(
                  <div key={imgIndex} className={classes.chatUploadedImgContainer}>
                    <div
                      onClick={() => removeUploadedTempImage(imgIndex, img.name)}
                      className={classes.chatUploadedImgRemoveIconContainer}
                    >
                      <ClearIcon className={classes.chatUploadedImgRemoveIcon} />
                    </div>
                    {isImage ?
                      <img src={img.url} width="80" alt={`${img.name ? img.name : 'uploaded-img'}`}/>
                      : <div className={classes.p10}>{img.name}</div>
                    }
                  </div>
                )
              })}
            </div>

              <Input
                placeholder="Enter message"
                className={classes.chatInput}
                value={chatMsg}
                multiline={true}
                onChange={(e) => onInputChange(selectedContactId, e.target.value)}
                disableUnderline={true}
                name="body"
                inputProps={isSmsSelected ? { maxLength: 1600 } : {}}
                onKeyDown={(e) => {
                  if (chatHotkeys === 1) {
                    sendWithEnterKeyDown(e);
                  } else if (chatHotkeys === 2) {
                    sendWithShiftEnterKeyDown(e);
                  } else if (chatHotkeys === 3) {
                    sendWithCtrlEnterKeyDown(e);
                  }
                }}
                onKeyUp={(e) => {
                  if (chatHotkeys === 1) {
                    sendWithEnterKeyUp(e);
                  } else if (chatHotkeys === 2) {
                    sendWithShiftEnterKeyUp(e);
                  } else if (chatHotkeys === 3) {
                    sendWithCtrlEnterKeyUp(e);
                  }
                }}
              />

            <div className={classes.chatBtnsContainer}>
              <div className={classes.alignCenter}>
                <Button
                  size="small"
                  variant="contained"
                  component="span"
                  className={classes.chatUploadBtn}
                  onClick={() => toggleUploadImgModal(true)}
                >
                  Attach
                </Button>

                {isSmsSelected || isFbSelected ? (
                  <div className={classes.chatSymbolsLeft}>Symbols left: {1600 - chatMsg.length}</div>
                ) : null}

                <div style={{ width: '150px' }}>
                  <TrplSelect
                    margin="none"
                    title="Template"
                    complicatedValues
                    value={validChatTemplate}
                    values={msgTemplatesValues}
                    onChange={onChatSelectChange}
                  />
                </div>

                {chatTemplatePids ? (
                  <div className={classes.chatPidsContainer}>
                    {chatTemplatePids.map((pid, index) => (
                      <div key={index} className={classes.chatPid}>
                        <span>{pid.title}</span>
                        <CloseIcon
                          onClick={() => onRemovePidClick(pid.id)}
                          className={classes.chatRemovePidIcon}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className={classes.alignCenter}>
                <Popover
                  open={isSendPopoverVisible}
                  anchorEl={chatSettingsAnchor}
                  anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  onClose={onChatSettingsClose}
                >
                  <div style={{ padding: '10px' }}>
                    <RadioGroup
                      value={chatHotkeys}
                      onChange={(e) => {
                        onChatRadioSelect(e.target.value);
                        
                      }}
                    >
                      <FormControlLabel
                        style={{ marginBottom: '20px' }}
                        value={1}
                        control={
                          <Radio/>
                        }
                        label={
                          <div>
                            <p style={{ marginTop: 0, marginBottom: '3px' }}>
                              <b>Enter</b> - send message
                            </p>
                            <p style={{ marginTop: 0, marginBottom: '3px' }}>
                              <b>Shift + Enter</b> - new line
                            </p>
                          </div>
                        }
                      />

                      <FormControlLabel
                        style={{ marginBottom: '20px' }}
                        value={2}
                        control={
                          <Radio/>
                        }
                        label={
                          <div>
                            <p style={{ marginTop: 0, marginBottom: '3px' }}>
                              <b>Shift + Enter</b> - send message
                            </p>
                            <p style={{ marginTop: 0, marginBottom: '3px' }}>
                              <b>Enter</b> - new line
                            </p>
                          </div>
                        }
                      />

                      <FormControlLabel
                        value={3}
                        control={
                          <Radio/>
                        }
                        label={
                          <div>
                            <p style={{ marginTop: 0, marginBottom: '3px' }}>
                              <b>Ctrl + Enter</b> - send message
                            </p>
                            <p style={{ marginTop: 0, marginBottom: '3px' }}>
                              <b>Enter</b> - new line
                            </p>
                          </div>
                        }
                      />
                    </RadioGroup>
                  </div>
                </Popover>

                {isSendingMsg ? (
                  <Loader />
                ) : (
                  <SettingsIcon
                    onClick={onChatSettingsOpen}
                    className={classes.chatSettingsIcon}
                    style={{ fontSize: 20 }}
                  />
                )}

                <Button
                  disabled={isDisabled}
                  size="small"
                  variant="contained"
                  type="button"
                  color="primary"
                  onClick={(e) => sendMsg(e)}
                  onSubmit={(e) => sendMsg(e)}
                  className={classes.chatSendBtn}
                >
                  Send
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Paper>
    );
  });
